import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { logout as logoutAdminSession } from "../../redux/slices/authSlice";
import { SESSION_COOLING_CODE } from "../utils/sessionCoolingHelpers";

export const APP_TOKEN_KEY = "betbond_app_token";

const getErrorMessage = (payload, fallbackError) => {
  try {
    if (payload) {
      if (Array.isArray(payload.errors)) {
        const msgs = payload.errors.map((e) => e?.msg || e?.message || e).filter(Boolean);
        if (msgs.length) return msgs.join(". ");
      }
      if (typeof payload.error === "string" && payload.error.trim()) {
        return payload.error;
      }
      if (typeof payload.message === "string" && payload.message.trim()) {
        return payload.message;
      }
    }
    if (fallbackError?.message) return fallbackError.message;
  } catch {
    /* ignore */
  }
  return "Something went wrong. Please try again.";
};

const persistToken = (token) => {
  if (token) localStorage.setItem(APP_TOKEN_KEY, token);
  else localStorage.removeItem(APP_TOKEN_KEY);
};

const fetchAuthUser = async (token) => {
  const userResponse = await axios.get(`${API_BASE_URL}/api/auth-check`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return {
    user: userResponse.data.user,
    sessionCooling: userResponse.data.sessionCooling,
  };
};

export const checkAppAuth = createAsyncThunk(
  "appAuth/checkAppAuth",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem(APP_TOKEN_KEY);
    if (!token) {
      return rejectWithValue({ silent: true });
    }

    try {
      const { user, sessionCooling } = await fetchAuthUser(token);
      return { user, token, sessionCooling };
    } catch (error) {
      const status = error.response?.status;
      const code = error.response?.data?.code;
      if (status === 401 || (status === 403 && code !== SESSION_COOLING_CODE)) {
        persistToken(null);
      }
      return rejectWithValue(error.response?.data || { error: "Auth check failed" });
    }
  }
);

export const loginAppUser = createAsyncThunk(
  "appAuth/loginAppUser",
  async ({ email, phoneNumber, password }, { rejectWithValue }) => {
    try {
      const payload = { password: (password || "").trim() };
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();

      const response = await axios.post(`${API_BASE_URL}/api/login`, payload);
      const { token } = response.data;
      persistToken(token);
      const { user, sessionCooling } = await fetchAuthUser(token);

      return { user, token, sessionCooling };
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Network error. Please try again." });
    }
  }
);

export const verifyAppEmail = createAsyncThunk(
  "appAuth/verifyAppEmail",
  async ({ email, phoneNumber, code }, { rejectWithValue }) => {
    try {
      const payload = { code: (code || "").trim() };
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();

      const response = await axios.post(`${API_BASE_URL}/api/verify-contact`, payload);
      const { token } = response.data;
      persistToken(token);
      const { user, sessionCooling } = await fetchAuthUser(token);

      return { user, token, sessionCooling };
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Verification failed. Please try again." });
    }
  }
);

export const resendAppVerificationCode = createAsyncThunk(
  "appAuth/resendAppVerificationCode",
  async ({ email, phoneNumber }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();

      await axios.post(`${API_BASE_URL}/api/resend-verification-code`, payload);
      return null;
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Failed to resend verification code." });
    }
  }
);

export const requestAppPasswordReset = createAsyncThunk(
  "appAuth/requestAppPasswordReset",
  async ({ email, phoneNumber, recoveryEmail }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();
      if (recoveryEmail) payload.recoveryEmail = recoveryEmail.trim().toLowerCase();

      const response = await axios.post(`${API_BASE_URL}/api/forgot-password`, payload);
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Failed to request password reset." });
    }
  }
);

export const performAppPasswordReset = createAsyncThunk(
  "appAuth/performAppPasswordReset",
  async ({ email, phoneNumber, recoveryEmail, resetCode, newPassword }, { rejectWithValue }) => {
    try {
      const payload = {
        resetCode: (resetCode || "").trim(),
        newPassword: (newPassword || "").trim(),
      };
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();
      if (recoveryEmail) payload.recoveryEmail = recoveryEmail.trim().toLowerCase();

      const response = await axios.post(`${API_BASE_URL}/api/reset-password`, payload);
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Failed to reset password." });
    }
  }
);

export const logoutAppUser = createAsyncThunk(
  "appAuth/logoutAppUser",
  async (_, { dispatch }) => {
    persistToken(null);
    dispatch(logoutAdminSession());
  }
);

export const signupAppUser = createAsyncThunk(
  "appAuth/signupAppUser",
  async ({ name, email, phoneNumber, password, referralCode }, { rejectWithValue }) => {
    try {
      const payload = {
        name: (name || "").trim(),
        password: (password || "").trim(),
      };
      if (email) payload.email = email.trim().toLowerCase();
      if (phoneNumber) payload.phoneNumber = phoneNumber.trim();
      if (referralCode) payload.referralCode = referralCode.trim();

      const response = await axios.post(`${API_BASE_URL}/api/signup`, payload);
      const data = response.data;

      if (data.token) {
        persistToken(data.token);
        const user = data.user || (await fetchAuthUser(data.token));
        return {
          user,
          token: data.token,
          immediate: true,
          email: payload.email || null,
          phoneNumber: payload.phoneNumber || null,
          message: data.message,
        };
      }

      return {
        email: payload.email || null,
        phoneNumber: payload.phoneNumber || null,
        contactMethod: payload.email ? "email" : "phone",
        message: data.message,
      };
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ error: "Network error. Please try again." });
    }
  }
);

const initialState = {
  user: null,
  token: localStorage.getItem(APP_TOKEN_KEY) || null,
  isAuthenticated: false,
  authReady: false,
  loading: false,
  error: null,
  requiresVerification: false,
  verificationEmail: null,
  verificationPhone: null,
};

const appAuthSlice = createSlice({
  name: "appAuth",
  initialState,
  reducers: {
    clearAppAuthError: (state) => {
      state.error = null;
    },
    patchAppUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAppAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAppAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.authReady = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(checkAppAuth.rejected, (state, action) => {
        state.loading = false;
        state.authReady = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload?.silent ? null : getErrorMessage(action.payload);
        if (!action.payload?.silent) {
          persistToken(null);
        }
      })

      .addCase(loginAppUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.verificationPhone = null;
      })
      .addCase(loginAppUser.fulfilled, (state, action) => {
        state.loading = false;
        state.authReady = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.verificationPhone = null;
      })
      .addCase(loginAppUser.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
        if (action.payload?.requiresVerification) {
          state.requiresVerification = true;
          state.verificationEmail = action.payload.email || null;
          state.verificationPhone = action.payload.phoneNumber || null;
        } else {
          state.requiresVerification = false;
          state.verificationEmail = null;
          state.verificationPhone = null;
        }
      })

      .addCase(verifyAppEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAppEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.authReady = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.verificationPhone = null;
      })
      .addCase(verifyAppEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
      })

      .addCase(resendAppVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendAppVerificationCode.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendAppVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
      })

      .addCase(requestAppPasswordReset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestAppPasswordReset.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestAppPasswordReset.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
      })

      .addCase(performAppPasswordReset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performAppPasswordReset.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(performAppPasswordReset.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
      })

      .addCase(logoutAppUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.verificationPhone = null;
      })

      .addCase(signupAppUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupAppUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        if (action.payload.immediate && action.payload.token) {
          state.isAuthenticated = true;
          state.authReady = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.requiresVerification = false;
          state.verificationEmail = null;
          state.verificationPhone = null;
          return;
        }

        state.verificationEmail = action.payload.email || null;
        state.verificationPhone = action.payload.phoneNumber || null;
        state.requiresVerification = true;
      })
      .addCase(signupAppUser.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action.payload);
      });
  },
});

export const { clearAppAuthError, patchAppUser } = appAuthSlice.actions;

export const selectAppAuth = (state) => state.appAuth;
export const selectAppUser = (state) => state.appAuth.user;
export const selectAppIsAuthenticated = (state) => state.appAuth.isAuthenticated;
export const selectAppAuthReady = (state) => state.appAuth.authReady;
export const selectAppAuthLoading = (state) => state.appAuth.loading;
export const selectAppAuthError = (state) => state.appAuth.error;
export const selectAppRequiresVerification = (state) => state.appAuth.requiresVerification;
export const selectAppVerificationEmail = (state) => state.appAuth.verificationEmail;
export const selectAppVerificationPhone = (state) => state.appAuth.verificationPhone;

export default appAuthSlice.reducer;
